import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';

import { Blocks } from '@/components/serie/Prose';
import { Accent } from '@/components/ui/Accent';
import { ButtonLink } from '@/components/ui/ButtonLink';
import { Container } from '@/components/ui/Container';
import { Label } from '@/components/ui/Label';
import { episodePath, episodes, findEpisode, findPhase, type Article } from '@/content/serie';
import { site } from '@/content/site';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { cn } from '@/lib/cn';

/** Les cinq sections fixes du format carottage (fil rouge). */
const SECTIONS = [
  { id: 'objectif', title: "Ce qu'on veut obtenir" },
  { id: 'installe', title: "Ce qu'on installe" },
  { id: 'mesure', title: "Ce qu'on mesure" },
  { id: 'casse', title: 'Ce qui casse' },
  { id: 'verdict', title: 'Le verdict' },
] as const;

/** Les quatre sections de corps ; le verdict a sa propre mise en page. */
const BODY_SECTIONS = SECTIONS.filter(
  (section): section is Extract<(typeof SECTIONS)[number], { id: Exclude<keyof Article, 'verdict'> }> =>
    section.id !== 'verdict',
);

export function EpisodePage() {
  const { slug } = useParams();
  const episode = slug ? findEpisode(slug) : undefined;
  const available = episode?.status === 'publie';

  // Les hooks passent avant toute sortie anticipée : méta neutre si l'épisode
  // n'existe pas ou n'est pas publié, puis redirection.
  useDocumentMeta({
    title: available ? `${episode.title}, ${site.name}` : site.name,
    description: (available && (episode.lead ?? episode.summary)) || site.description,
    path: available ? episodePath(episode) : '/serie',
  });

  if (!episode || !available) return <Navigate to="/serie" replace />;

  const phase = findPhase(episode.phase);
  const index = episodes.indexOf(episode);
  const previous = episodes[index - 1];
  const next = episodes[index + 1];

  const meta = [
    { label: 'Publié le', value: episode.date },
    { label: 'Testé avec', value: episode.testedWith },
    { label: 'Lecture', value: episode.readingTime && `${String(episode.readingTime)} min` },
    { label: 'Vidéo', value: episode.videoMinutes && `${String(episode.videoMinutes)} min, sans montage` },
  ].filter((item) => item.value);

  return (
    <>
      <div className="pt-11 pb-8 tab:pt-18 tab:pb-12">
        <Container>
          <div className="flex flex-wrap gap-2.5">
            <Chip>Épisode {episode.num}</Chip>
            {phase && (
              <Chip>
                Phase {phase.num}, {phase.title.toLowerCase()}
              </Chip>
            )}
          </div>

          <h1 className="mt-6 max-w-[12em] text-title">
            <Accent text={episode.headline ?? episode.title} />
          </h1>

          {(episode.lead ?? episode.summary) && (
            <p className="mt-4.5 max-w-[34em] text-lead text-encre-soft">
              {episode.lead ?? episode.summary}
            </p>
          )}

          <dl className="m-0 mt-9 flex flex-wrap gap-9 border-t border-line pt-5">
            {meta.map((item) => (
              <div key={item.label} className="flex flex-col gap-1">
                <dt className="label text-encre-mute">{item.label}</dt>
                <dd className="m-0 text-body-2xs font-medium tabular-nums">{item.value}</dd>
              </div>
            ))}
            {episode.repo && (
              <div className="flex flex-col gap-1">
                <dt className="label text-encre-mute">Code</dt>
                <dd className="m-0 text-body-2xs font-medium">
                  <a href={`https://github.com/${episode.repo}`}>{episode.repo}</a>
                </dd>
              </div>
            )}
          </dl>
        </Container>
      </div>

      {episode.article ? (
        <ArticleBody article={episode.article} repo={episode.repo} />
      ) : (
        <Container className="border-t border-line-soft pt-14 pb-20">
          {/* Épisode publié dont le texte n'est pas encore saisi ici. */}
          <p className="max-w-[42em] text-prose text-encre-soft">Le texte de cet épisode arrive.</p>
        </Container>
      )}

      <Container className="grid gap-5 pb-22 tab:grid-cols-2">
        {previous ? <NextLink episode={previous} direction="previous" /> : <span />}
        {next && <NextLink episode={next} direction="next" />}
      </Container>
    </>
  );
}

function ArticleBody({ article, repo }: { article: Article; repo?: string }) {
  const active = useActiveSection();

  return (
    <Container className="grid grid-cols-[minmax(0,1fr)] gap-0 border-t border-line-soft pt-14 pb-20 desk:grid-cols-[200px_minmax(0,1fr)] desk:gap-18">
      <nav aria-label="Sommaire" className="sticky top-26 hidden self-start desk:flex desk:flex-col">
        {SECTIONS.map((section) => (
          <a
            key={section.id}
            href={`#${section.id}`}
            className={cn(
              'border-l-2 py-2 pl-3.5 text-body-xs no-underline',
              active === section.id
                ? 'border-ciel text-encre'
                : 'border-line-soft text-encre-soft hover:border-ciel hover:text-encre',
            )}
          >
            {section.title}
          </a>
        ))}
      </nav>

      <article className="max-w-[42em] text-prose text-encre/92">
        {BODY_SECTIONS.map((section, index) => (
          <section key={section.id}>
            <ProseHeading id={section.id} num={index + 1} title={section.title} />
            <Blocks blocks={article[section.id]} />
          </section>
        ))}

        <section>
          <ProseHeading id="verdict" num={5} title="Le verdict" />
          <dl className="m-0 grid gap-5 rounded-xl bg-encre px-5.5 py-6 text-blanc tab:px-8.5 tab:py-8">
            {[
              { label: 'Ce que ça fait bien', value: article.verdict.bien },
              { label: 'Ce que ça casse', value: article.verdict.casse },
              { label: 'Chez un client ?', value: article.verdict.client },
            ].map((row) => (
              <div
                key={row.label}
                className="grid gap-1.5 border-b border-blanc/14 pb-5 last:border-0 last:pb-0 tab:grid-cols-[190px_minmax(0,1fr)] tab:gap-5"
              >
                <dt className="label text-ciel">{row.label}</dt>
                <dd className="m-0 text-sub">{row.value}</dd>
              </div>
            ))}
          </dl>
        </section>

        {repo && (
          <div className="mt-9 flex flex-wrap items-center justify-between gap-5 rounded-md border border-line px-5.5 py-5">
            <div>
              <Label>Code de l&apos;épisode</Label>
              <b className="mt-1 block text-body-s font-medium">github.com/{repo}</b>
            </div>
            <ButtonLink to={`https://github.com/${repo}`} variant="ligne">
              Voir le repo →
            </ButtonLink>
          </div>
        )}
      </article>
    </Container>
  );
}

function ProseHeading({ id, num, title }: { id: string; num: number; title: string }) {
  return (
    <h2
      id={id}
      className="mt-15 mb-4 flex scroll-mt-25 items-baseline gap-3.5 text-prose-h2 text-encre [section:first-child>&]:mt-0"
    >
      <span className="rounded-xs bg-ciel px-2 py-[5px] text-num font-medium tabular-nums">
        {String(num).padStart(2, '0')}
      </span>
      {title}
    </h2>
  );
}

function Chip({ children }: { children: React.ReactNode }) {
  return <span className="label rounded-pill bg-blanc px-3 py-2 text-tag text-encre">{children}</span>;
}

function NextLink({ episode, direction }: { episode: (typeof episodes)[number]; direction: 'previous' | 'next' }) {
  const todo = episode.status !== 'publie';
  const label =
    direction === 'previous'
      ? `← Épisode ${episode.num}`
      : `Épisode ${episode.num}${todo && episode.planned ? `, ${episode.planned}` : ''} →`;
  const classes = cn(
    'flex flex-col gap-2 rounded-lg px-6.5 py-6 text-encre no-underline',
    direction === 'next' && 'text-right',
    todo ? 'border border-line' : 'bg-blanc',
  );
  const body = (
    <>
      <Label>{label}</Label>
      <b className={cn('text-h4', todo && 'text-encre-mute')}>{episode.title}</b>
    </>
  );

  return todo ? (
    <div className={classes}>{body}</div>
  ) : (
    <Link to={episodePath(episode)} className={classes}>
      {body}
    </Link>
  );
}

/** Section visible, pour le sommaire : la dernière dont le titre a passé le haut de l'écran. */
function useActiveSection(): string {
  const [active, setActive] = useState<string>(SECTIONS[0].id);

  useEffect(() => {
    const onScroll = () => {
      let current: string = SECTIONS[0].id;
      for (const section of SECTIONS) {
        const element = document.getElementById(section.id);
        if (element && element.getBoundingClientRect().top < 140) current = section.id;
      }
      setActive(current);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return active;
}
